import React, { useRef, useState } from 'react';
import { uploadAdminMedia } from '../services/adminApi';
import {
  normalizeDigitalOceanSpacesCdnUrl,
  resolveMediaUrlForEmbed,
  resolveUrlForImageLoad
} from '../utils/adminMediaUrls';

 const IMAGE_EXT = /\.(png|jpg|jpeg|webp|gif)(\?.*)?$/i;
//-const VIDEO_EXT = /\.(mp4|webm|m3u8|mov)(\?.*)?$/i;
const VIDEO_EXT = /\.(mp4|webm|m3u8|mov|avi|mkv)(\?.*)?$/i;
 


const MediaUploadInput = ({
  label,
  value,
  onChange,
  accept = 'image/*',
  placeholder = 'https://...',
  helperText = '',
  maxSizeMb = 30,
  /** When false, only the text field + drop zone render (no extra img/video under the input). */
  showInlinePreview = true,
  /** Validation message from parent form (shown with .form-error-text). */
  externalError = ''
}) => {
  const fileRef = useRef(null);
  const [isUploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [error, setError] = useState('');
  const [isDragOver, setDragOver] = useState(false);

  const uploadFile = async (file) => {
    if (!file) return;
    if (accept.includes('image') && !String(file.type || '').startsWith('image/')) {
      setError('Please select an image file.');
      return;
    }
    if (accept.includes('video') && !String(file.type || '').startsWith('video/')) {
      setError('Please select a video file.');
      return;
    }
    if (file.size > maxSizeMb * 1024 * 1024) {
      setError(`File is too large. Max allowed is ${maxSizeMb}MB.`);
      return;
    }
    setUploading(true);
    setUploadPct(0);
    setError('');
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await uploadAdminMedia(form, (evt) => {
        const total = evt?.total || 0;
        if (!total) return;
        setUploadPct(Math.max(0, Math.min(100, Math.round((evt.loaded * 100) / total))));
      });
      const url = res?.data?.url || '';
      if (url) onChange(normalizeDigitalOceanSpacesCdnUrl(url));
      else setError('Upload succeeded but URL was not returned.');
    } catch (e) {
      setError(e?.response?.data?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    uploadFile(file);
  };

  const normalized = String(value || '').trim();
  const looksMediaPath = /^https?:\/\//i.test(normalized) || normalized.startsWith('/uploads/');
  const looksImage = looksMediaPath && IMAGE_EXT.test(normalized);
  const looksVideo = looksMediaPath && VIDEO_EXT.test(normalized);
  const isImageField = accept.includes('image');
  const isVideoField = accept.includes('video');
  /**
   * Avoid showing the wrong preview type.
   * If a URL doesn't clearly look like an image/video (no extension), don't guess — show no inline preview.
   */
  const showInlineImage = showInlinePreview && isImageField && looksImage && !looksVideo;
  const showInlineVideo = showInlinePreview && isVideoField && looksVideo && !looksImage;

  const inlinePreviewSrc = showInlineImage
    ? resolveUrlForImageLoad(normalized)
    : showInlineVideo
      ? resolveMediaUrlForEmbed(normalized, 'video')
      : '';
  const acceptText = accept.includes('video') ? 'video' : accept.includes('image') ? 'image' : 'media';
  const formatsNote = isVideoField
    ? 'For video - .mp4, .avi, .mkv'
    : isImageField
      ? 'For image - .jpg, .jpeg, .png, .gif, .webp'
      : '';

  return (
    <div className="form-group">
      <label>{label}</label>
      {helperText ? <div className="admin-service-media-drive-hint">{helperText}</div> : null}
      <input
        type="text"
        inputMode="url"
        autoComplete="off"
        spellCheck={false}
        placeholder={placeholder}
        value={value || ''}
        className={externalError ? 'input-error' : undefined}
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => {
          const fixed = normalizeDigitalOceanSpacesCdnUrl(value);
          if (fixed !== String(value || '')) onChange(fixed);
        }}
      />
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => fileRef.current?.click()}
        style={{
          marginTop: 8,
          padding: '10px 12px',
          border: `1px dashed ${isDragOver ? '#58c4dc' : '#4b5261'}`,
          borderRadius: 8,
          color: '#cbd2df',
          cursor: 'pointer',
          background: isDragOver ? 'rgba(88,196,220,0.08)' : 'transparent'
        }}
      >
        {isUploading ? `Uploading... ${uploadPct}%` : `Drag & drop ${acceptText} here, or click to upload`}
      </div>
      <div style={{ marginTop: 6, fontSize: 12, color: '#9ea8b7' }}>
        {formatsNote ? (
          <>
            Accepted formats: {formatsNote}. Max size: {maxSizeMb}MB.
          </>
        ) : (
          <>
            Max size: {maxSizeMb}MB.
          </>
        )}
      </div>
      <div style={{ marginTop: 4, fontSize: 12, color: '#9ea8b7' }}>
        Tip: compress large files (images to JPG/WebP, videos to MP4) for faster upload and playback.
      </div>
      <input
        ref={fileRef}
        type="file"
        accept={accept}
        onChange={(e) => uploadFile(e.target.files?.[0])}
        style={{ display: 'none' }}
      />
      {showInlineImage ? (
        <img
          key={inlinePreviewSrc || normalized}
          src={inlinePreviewSrc || normalized}
          alt="preview"
          style={{ marginTop: 8, maxHeight: 120, borderRadius: 6, objectFit: 'cover' }}
          referrerPolicy="no-referrer"
        />
      ) : showInlineVideo ? (
        <video
          key={inlinePreviewSrc || normalized}
          src={inlinePreviewSrc || normalized}
          controls
          muted
          preload="metadata"
          style={{ marginTop: 8, maxHeight: 140, width: '100%', borderRadius: 6 }}
          referrerPolicy="no-referrer"
        />
      ) : null}
      {(externalError || error) ? (
        <span className="form-error-text" style={{ display: 'block', marginTop: 6 }}>
          {externalError || error}
        </span>
      ) : null}
    </div>
  );
};

export default MediaUploadInput;
